import { type Number3 } from '../types';

export enum VisibilityBoxMode {
  Disabled,
  Enabled,
  Adjusting,
}

export type VisibilityBoxControlStyle = {
  boxFrame?: { color: number; lineWidth: number };
  draggingBox?: {
    color: number;
    size: number;
    draggingColor: number;
  };
};

export type VisibilityBoxStyle = VisibilityBoxControlStyle & {
  grayArea?: {
    multiply: number;
    additiveColor: number;
    alpha: number;
  };
};

export const defaultVisibilityBoxStyle = {
  grayArea: {
    multiply: 0.2,
    additiveColor: 0x202020,
    alpha: 1,
  },
  boxFrame: { color: 0xffff80, lineWidth: 1 },
  draggingBox: {
    color: 0x00ff00,
    size: 2,
    draggingColor: 0xff2222,
  },
};

export const defaultImageAnchorsExtraRange: Number3 = [1, 1, 0.5];

export type Transform = {
  position: Number3;
  rotation: Number3;
  scale: Number3;
};

export type VisibilityBoxBaseProps = {
  mode: VisibilityBoxMode;
  step: Number3;
  style?: VisibilityBoxStyle;
};

export type AlignedBox = {
  min: Number3;
  max: Number3;
};

export type AlignedVisibilityBoxProps = VisibilityBoxBaseProps & AlignedBox;
export type RotatedVisibilityBoxProps = VisibilityBoxBaseProps & Transform;

// export type VisibilityBoxProps = AlignedVisibilityBoxProps | RotatedVisibilityBoxProps;
export type VisibilityBoxProps = RotatedVisibilityBoxProps;

export const defaultVisibilityBox: RotatedVisibilityBoxProps = {
  mode: VisibilityBoxMode.Disabled,
  position: [0, 0, 0],
  rotation: [0, 0, 0],
  scale: [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY],
  step: [0.25, 0.25, 0.1],
};

export const transformFromAlignedBox = (box: AlignedBox): Transform => {
  const { min, max } = box;
  const position: Number3 = [
    (min[0] + max[0]) * 0.5,
    (min[1] + max[1]) * 0.5,
    (min[2] + max[2]) * 0.5,
  ];
  const rotation: Number3 = [0, 0, 0];
  const scale: Number3 = [max[0] - min[0], max[1] - min[1], max[2] - min[2]];
  return { position, rotation, scale };
};

export const rotatedVisibilityBoxFromAligned = (
  vbox: AlignedVisibilityBoxProps
): RotatedVisibilityBoxProps => {
  const { mode, step } = vbox;
  const { position, rotation, scale } = transformFromAlignedBox(vbox);
  return { mode, position, rotation, scale, step };
};

export const alignedBoxFromTransform = (transform: Transform): AlignedBox => {
  const { position, rotation, scale } = transform;
  if (rotation[0] !== 0 || rotation[1] !== 0 || rotation[2] !== 0) {
    throw new Error('Unable to convert rotated transform to aligned box');
  }
  const min: Number3 = [
    position[0] - scale[0] * 0.5,
    position[1] - scale[1] * 0.5,
    position[2] - scale[2] * 0.5,
  ];
  const max: Number3 = [
    position[0] + scale[0] * 0.5,
    position[1] + scale[1] * 0.5,
    position[2] + scale[2] * 0.5,
  ];
  return { min, max };
};
